// src/core/ranking.js — capa de datos del ranking.
//
// FASE 4 · F1.4.b. Lee `puntos_prode` + `puntos_rituales` joineados con
// `jugadores` (display_name + group_id). Devuelve filas adaptadas al shape
// que el render de modules/ranking.js espera: { jugadorId, apodo, groupId,
// general, prode, rituales, breakdown }.
//
// Niveles:
//   - 'grupo'       → solo jugadores del grupo del usuario activo.
//   - 'intergrupal' → todos los jugadores; agrupados por grupo (visualizable).
//   - 'nacional'    → todos los jugadores.
//
// Categorías:
//   - 'general'  → puntos_prode.puntos_total + puntos_rituales.puntos_total.
//   - 'prode'    → puntos_prode.puntos_total.
//   - 'rituales' → puntos_rituales.puntos_total.
//
// Cache TTL 60s (mismo patrón que core/rituales.js y core/tribuna.js). Se
// invalida tras eventos realtime sobre puntos_prode / puntos_rituales.

import { supabase } from '../supabase.js'
import { queryWithTimeout, NET_TIMEOUT_MS } from '../utils/timeout.js'

const RANKING_CACHE_TTL_MS = 60_000

// DT-H3-resto (6-jun): los fetches del ranking con timeout/abort. La carga
// PRIMARIA lanza en error/timeout (la vista muestra "Reintentar"); el resto
// degrada. Helper local que aplica .abortSignal al query builder.
const conTimeout = (label, buildQuery) =>
  queryWithTimeout((signal) => buildQuery(signal), NET_TIMEOUT_MS, label)

const memoConTTL = (ttl) => {
  const cache = new Map()
  const inflight = new Map()
  return {
    run: async (clave, fn) => {
      const hit = cache.get(clave)
      if (hit && Date.now() - hit.at < ttl) return hit.value
      if (inflight.has(clave)) return inflight.get(clave)
      const promise = (async () => {
        try {
          const value = await fn()
          cache.set(clave, { value, at: Date.now() })
          return value
        } finally {
          inflight.delete(clave)
        }
      })()
      inflight.set(clave, promise)
      return promise
    },
    clear: () => { cache.clear(); inflight.clear() },
  }
}

const _rankingMemo = memoConTTL(RANKING_CACHE_TTL_MS)

export const invalidarCacheRanking = () => _rankingMemo.clear()

// Carga la tabla completa de puntos joineada con jugadores. Cachea por clave
// `scope` ('grupo:<id>' | 'nacional' | 'intergrupal'). El render decide cómo
// agrupar / mostrar.
const cargarRanking = (scope, groupId = null) => _rankingMemo.run(scope, async () => {
  // Ranking v1.1 §11.4: para Nacional necesitamos user_id (dedupe por persona)
  // y joined_at (apodo del primer player_id). Para Intergrupal necesitamos
  // grupos.code (subtítulo §3.4). Joineamos puntos_* por player_id.
  let jugadoresQ = supabase
    .from('jugadores')
    .select('player_id, user_id, group_id, display_name, joined_at, grupos(name, code)')
  if (scope.startsWith('grupo:') && groupId) {
    jugadoresQ = jugadoresQ.eq('group_id', groupId)
  }
  const [jugRes, prodeRes, ritRes] = await Promise.all([
    conTimeout('ranking-jugadores', (s) => jugadoresQ.abortSignal(s)),
    // Ranking v1.2 §2.2/§6.1: además del puntaje, traemos descripción y timestamp
    // de la última movida de Prode (para subtítulo "Últ. mov" en Mi Grupo).
    conTimeout('ranking-prode', (s) => supabase.from('puntos_prode').select('player_id, puntos_total, ultima_movida_puntos, ultima_movida_descripcion, ultima_movida_at, aciertos_completos_playoffs, partidos_jugados').abortSignal(s)),
    // Ranking v1.2 §2.4/§6.1: idem para rituales (puede venir vacío si el jugador
    // todavía no creó/reaccionó/incumplió ningún ritual).
    conTimeout('ranking-rituales', (s) => supabase.from('puntos_rituales').select('player_id, puntos_total, ultima_movida_puntos, ultima_movida_descripcion, ultima_movida_at').abortSignal(s)),
  ])
  // Primario: si jugadores falla/timeoutea, PROPAGA → la vista muestra "Reintentar".
  if (jugRes.error) { console.error('[ranking] jugadores:', jugRes.error.message); throw new Error(`ranking-jugadores: ${jugRes.error.message}`) }
  const prodeMap = new Map((prodeRes.data || []).map((r) => [r.player_id, r]))
  const ritMap = new Map((ritRes.data || []).map((r) => [r.player_id, r]))
  return (jugRes.data || []).map((j) => {
    const p = prodeMap.get(j.player_id) || {}
    const r = ritMap.get(j.player_id) || {}
    const prode = p.puntos_total || 0
    const rituales = r.puntos_total || 0
    return {
      jugadorId: j.player_id,
      userId: j.user_id,
      apodo: j.display_name,
      groupId: j.group_id,
      grupoNombre: j.grupos?.name || '',
      grupoCode: j.grupos?.code || '',
      joinedAt: j.joined_at,
      prode,
      rituales,
      general: prode + rituales,
      // Campos de desempate (§17.2):
      ultimaMovidaPuntos: p.ultima_movida_puntos || 0,
      aciertosCompletosPlayoffs: p.aciertos_completos_playoffs || 0,
      partidosJugados: p.partidos_jugados || 0,
      // Ranking v1.2 §6.2: última movida split Prode vs Ritual, para que el
      // render pueda elegir qué mostrar según el filtro activo en Mi Grupo.
      ultimaMovidaProdePuntos: p.ultima_movida_puntos ?? null,
      ultimaMovidaProdeDescripcion: p.ultima_movida_descripcion || '',
      ultimaMovidaProdeAt: p.ultima_movida_at || null,
      ultimaMovidaRitualPuntos: r.ultima_movida_puntos ?? null,
      ultimaMovidaRitualDescripcion: r.ultima_movida_descripcion || '',
      ultimaMovidaRitualAt: r.ultima_movida_at || null,
    }
  })
})

// Devuelve el ranking ordenado por categoría, descendente.
// Spec Prode_Scoring v1.1 §17.2 — desempates secuenciales (sólo para Prode):
//   1. Puntos totales (DESC)
//   2. Puntos del último juego (DESC)
//   3. Aciertos completos en playoffs (DESC)
//   4. Menos partidos jugados (ASC)
//   5. Empate real (orden estable).
// "general" y "rituales" usan solo criterio 1 (no aplican §17.2, que es Prode).
export const ordenarPor = (filas, categoria) => {
  const key = categoria === 'prode' ? 'prode' : categoria === 'rituales' ? 'rituales' : 'general'
  return [...filas].sort((a, b) => {
    const dPrim = b[key] - a[key]
    if (dPrim !== 0) return dPrim
    if (categoria !== 'prode') return 0 // empate real
    // §17.2 criterio 2: último juego (DESC).
    const dUlt = (b.ultimaMovidaPuntos || 0) - (a.ultimaMovidaPuntos || 0)
    if (dUlt !== 0) return dUlt
    // §17.2 criterio 3: aciertos completos playoffs (DESC).
    const dAc = (b.aciertosCompletosPlayoffs || 0) - (a.aciertosCompletosPlayoffs || 0)
    if (dAc !== 0) return dAc
    // §17.2 criterio 4: menos partidos jugados (ASC).
    const dPj = (a.partidosJugados || 0) - (b.partidosJugados || 0)
    if (dPj !== 0) return dPj
    return 0 // §17.2 criterio 5: empate real
  })
}

// Mi grupo: solo jugadores del grupo activo.
export const getRankingMiGrupo = async (groupId, categoria = 'general') => {
  if (!groupId) return []
  const filas = await cargarRanking(`grupo:${groupId}`, groupId)
  return ordenarPor(filas, categoria)
}

// Nacional v1.1 §4.2: dedupe por user_id (1 persona = 1 fila). Llama al RPC
// SECURITY DEFINER que bypassa el RLS de jugadores/grupos (que limita
// visibilidad a grupos del usuario activo). El ranking nacional necesita ver
// TODOS los jugadores únicos del país, no solo los que comparten grupo.
export const getRankingNacional = async (categoria = 'general') => {
  const { data, error } = await conTimeout('ranking_nacional', (s) => supabase.rpc('ranking_nacional').abortSignal(s))
  if (error) { console.error('[ranking] ranking_nacional:', error.message); throw new Error(`ranking_nacional: ${error.message}`) }
  const filas = (data || []).map((r) => ({
    userId: r.user_id,
    jugadorId: r.jugador_id,
    apodo: r.apodo,
    grupos: Array.isArray(r.grupos) ? r.grupos.map((g) => ({ groupId: g.group_id, nombre: g.nombre, code: g.code })) : [],
    prode: r.prode || 0,
    rituales: r.rituales || 0,
    general: r.general || 0,
    ultimaMovidaPuntos: r.ultima_movida_puntos || 0,
    aciertosCompletosPlayoffs: r.aciertos_completos_playoffs || 0,
    partidosJugados: r.partidos_jugados || 0,
  }))
  return ordenarPor(filas, categoria)
}

// Intergrupal v1.1 §3: TODOS los grupos del país rankeados por promedio. Llama
// al RPC SECURITY DEFINER que bypassa RLS (grupos_select limita a los del user).
export const getRankingIntergrupal = async (categoria = 'general') => {
  const { data, error } = await conTimeout('ranking_intergrupal', (s) => supabase.rpc('ranking_intergrupal').abortSignal(s))
  if (error) { console.error('[ranking] ranking_intergrupal:', error.message); throw new Error(`ranking_intergrupal: ${error.message}`) }
  const filasGrupos = (data || []).map((r) => ({
    groupId: r.group_id,
    grupoNombre: r.grupo_nombre,
    grupoCode: r.grupo_code,
    jugadores: r.jugadores || 0,
    promedio: r.promedio || 0,
    promedioProde: r.promedio_prode || 0,
    promedioRituales: r.promedio_rituales || 0,
  }))
  const key = categoria === 'prode' ? 'promedioProde' : categoria === 'rituales' ? 'promedioRituales' : 'promedio'
  return [...filasGrupos].sort((a, b) => b[key] - a[key])
}

// Puntos de UN jugador (DT-CQ, F1.4.b.2). Lee puntos_prode + puntos_rituales
// directo y los suma. Usado por modules/perfil.js para el banner superior.
export const getPuntosJugador = async (playerId) => {
  if (!playerId) {
    return { prode: 0, rituales: 0, total: 0, totalCreaciones: 0, totalReaccionesRecibidas: 0, promesasCumplidas: 0, promesasIncumplidas: 0 }
  }
  let prodeRes = {}, ritRes = {}
  try {
    [prodeRes, ritRes] = await Promise.all([
      conTimeout('puntos_prode-jug', (s) => supabase.from('puntos_prode').select('puntos_total').eq('player_id', playerId).abortSignal(s).maybeSingle()),
      conTimeout('puntos_rituales-jug', (s) => supabase.from('puntos_rituales').select('puntos_total, total_creaciones, total_reacciones_recibidas, promesas_cumplidas, promesas_incumplidas').eq('player_id', playerId).abortSignal(s).maybeSingle()),
    ])
  } catch (err) { console.error('[ranking] getPuntosJugador timeout:', err?.message || err) } // banner secundario → degrada a ceros
  const prode = prodeRes.data?.puntos_total || 0
  const rituales = ritRes.data?.puntos_total || 0
  return {
    prode,
    rituales,
    total: prode + rituales,
    totalCreaciones: ritRes.data?.total_creaciones || 0,
    totalReaccionesRecibidas: ritRes.data?.total_reacciones_recibidas || 0,
    promesasCumplidas: ritRes.data?.promesas_cumplidas || 0,
    promesasIncumplidas: ritRes.data?.promesas_incumplidas || 0,
  }
}

// Devuelve true si TODOS los puntos son 0 (pre-Mundial). El render usa esto
// para mostrar el empty state "El ranking arranca el 11 de junio". Soporta
// el shape de jugadores (`general`) y el shape intergrupal (`promedio`).
export const rankingEstaVacio = (filas) => {
  if (filas.length === 0) return true
  return filas.every((f) => (f.general || 0) === 0 && (f.promedio || 0) === 0)
}
